import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BookingHistory = ({ user, onDataChange }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Mock booking data - in real app this would come from API
  const bookings = [
    {
      id: 1,
      type: 'EES Session',
      center: 'Harmony Wellness Center',
      practitioner: 'Dr. Sarah Johnson',
      date: '2024-01-20',
      time: '10:00 AM',
      duration: 90,
      status: 'completed',
      rating: 5,
      review: 'Absolutely transformative experience. I felt deeply relaxed and energized.',
      energyBefore: 6,
      energyAfter: 9,
      cost: 120,
      location: 'Los Angeles, CA',
      sessionNotes: 'Focused on stress reduction and energy enhancement. Client responded very well to frequency adjustments.',
      outcomes: ['Increased energy', 'Reduced stress', 'Better sleep']
    },
    {
      id: 2,
      type: 'Meditation Retreat',
      center: 'Zenith Retreat Center',
      practitioner: 'Master Chen Wei',
      date: '2024-01-18',
      time: '9:00 AM',
      duration: 180,
      status: 'completed',
      rating: 4,
      review: 'Wonderful meditation experience with great group energy.',
      energyBefore: 5,
      energyAfter: 8,
      cost: 85,
      location: 'San Diego, CA',
      sessionNotes: 'Group meditation session focusing on mindfulness and breath work.',
      outcomes: ['Mental clarity', 'Emotional balance']
    },
    {
      id: 3,
      type: 'Sound Healing',
      center: 'Sacred Sound Studio',
      practitioner: 'Luna Martinez',
      date: '2024-01-15',
      time: '2:00 PM',
      duration: 60,
      status: 'completed',
      rating: 5,
      review: 'The crystal bowl healing was absolutely divine. Highly recommended!',
      energyBefore: 4,
      energyAfter: 8,
      cost: 75,
      location: 'Santa Monica, CA',
      sessionNotes: 'Sound healing with Tibetan bowls and crystal singing bowls.',
      outcomes: ['Deep relaxation', 'Stress relief', 'Spiritual connection']
    },
    {
      id: 4,
      type: 'EES Session',
      center: 'Quantum Healing Center',
      practitioner: 'Dr. Michael Smith',
      date: '2024-01-22',
      time: '3:00 PM',
      duration: 120,
      status: 'upcoming',
      cost: 150,
      location: 'Beverly Hills, CA'
    },
    {
      id: 5,
      type: 'Chakra Balancing',
      center: 'Inner Light Wellness',
      practitioner: 'Maya Patel',
      date: '2024-01-10',
      time: '11:00 AM',
      duration: 90,
      status: 'cancelled',
      cost: 95,
      location: 'West Hollywood, CA',
      cancellationReason: 'Practitioner illness'
    }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Sessions' },
    { value: 'completed', label: 'Completed' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const filteredBookings = bookings.filter(booking => 
    selectedFilter === 'all' || booking.status === selectedFilter
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'emerald';
      case 'upcoming': return 'blue';
      case 'cancelled': return 'red';
      default: return 'slate';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'upcoming': return 'Clock';
      case 'cancelled': return 'XCircle';
      default: return 'Circle';
    }
  };

  const handleRebook = (booking) => {
    // In real app, this would navigate to booking page with pre-filled data
    console.log('Rebooking session:', booking);
  };

  const handleReview = (booking) => {
    // In real app, this would open review modal
    console.log('Adding review for:', booking);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Icon
        key={i}
        name="Star"
        size={14}
        className={i < rating ? 'text-yellow-400 fill-current' : 'text-slate-300'}
      />
    ));
  };

  return (
    <div className="space-y-6">
      {/* Filter Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="Calendar" size={20} className="mr-2 text-blue-600" />
            Booking History & Upcoming Sessions
          </h2>
          
          <div className="flex items-center space-x-3">
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="border border-blue-200 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
              Export History
            </button>
          </div>
        </div>
      </div>

      {/* Booking Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {bookings.filter(b => b.status === 'completed').length}
              </p>
              <p className="text-sm text-slate-600">Completed Sessions</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-emerald-500" />
          </div>
        </div>
        
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {bookings.filter(b => b.status === 'upcoming').length}
              </p>
              <p className="text-sm text-slate-600">Upcoming Sessions</p>
            </div>
            <Icon name="Clock" size={24} className="text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {bookings.filter(b => b.status === 'completed').reduce((total, b) => total + (b.duration || 0), 0)}
              </p>
              <p className="text-sm text-slate-600">Total Minutes</p>
            </div>
            <Icon name="Clock" size={24} className="text-purple-500" />
          </div>
        </div>
        
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {bookings.filter(b => b.rating).reduce((total, b, _, arr) => total + b.rating / arr.length, 0).toFixed(1)}
              </p>
              <p className="text-sm text-slate-600">Average Rating</p>
            </div>
            <Icon name="Star" size={24} className="text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Booking List */}
      <div className="space-y-4">
        {filteredBookings.map((booking) => (
          <div key={booking.id} className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100 overflow-hidden">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Main Booking Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-1">{booking.type}</h3>
                      <p className="text-slate-600">{booking.center}</p>
                      <p className="text-sm text-slate-500">{booking.location}</p>
                    </div>
                    
                    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full bg-${getStatusColor(booking.status)}-100 text-${getStatusColor(booking.status)}-800`}>
                      <Icon name={getStatusIcon(booking.status)} size={14} />
                      <span className="text-xs font-medium capitalize">{booking.status}</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-slate-600">
                    <div>
                      <span className="block font-medium">Date & Time</span>
                      <span>{new Date(booking.date).toLocaleDateString()}</span>
                      <span className="block">{booking.time}</span>
                    </div>
                    
                    <div>
                      <span className="block font-medium">Practitioner</span>
                      <span>{booking.practitioner}</span>
                    </div>
                    
                    <div>
                      <span className="block font-medium">Duration</span>
                      <span>{booking.duration} minutes</span>
                    </div>
                    
                    <div>
                      <span className="block font-medium">Cost</span>
                      <span>${booking.cost}</span>
                    </div>
                  </div>
                  
                  {/* Energy Levels (for completed sessions) */}
                  {booking.status === 'completed' && booking.energyBefore && booking.energyAfter && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600">Energy Level:</span>
                          <span className="font-medium text-slate-800">
                            {booking.energyBefore} → {booking.energyAfter}
                          </span>
                          <Icon name="TrendingUp" size={16} className="text-emerald-500" />
                        </div>
                        
                        {booking.outcomes && booking.outcomes.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-slate-600">Outcomes:</span>
                            <div className="flex flex-wrap gap-1">
                              {booking.outcomes.map((outcome, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-800">
                                  {outcome}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Review (for completed sessions with reviews) */}
                  {booking.review && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-start space-x-3">
                        <div className="flex space-x-1">
                          {renderStars(booking.rating)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-700 italic">"{booking.review}"</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Cancellation reason */}
                  {booking.status === 'cancelled' && booking.cancellationReason && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-800">
                        <strong>Cancellation reason:</strong> {booking.cancellationReason}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex lg:flex-col space-x-3 lg:space-x-0 lg:space-y-2">
                  {booking.status === 'completed' && (
                    <>
                      <button
                        onClick={() => handleRebook(booking)}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
                      >
                        <Icon name="RotateCcw" size={14} />
                        <span>Rebook</span>
                      </button>
                      
                      <button
                        onClick={() => setSelectedBooking(booking)}
                        className="flex items-center space-x-2 px-4 py-2 border border-blue-200 text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors duration-200"
                      >
                        <Icon name="Eye" size={14} />
                        <span>Details</span>
                      </button>
                      
                      {!booking.review && (
                        <button
                          onClick={() => handleReview(booking)}
                          className="flex items-center space-x-2 px-4 py-2 border border-emerald-200 text-emerald-600 rounded-md text-sm font-medium hover:bg-emerald-50 transition-colors duration-200"
                        >
                          <Icon name="MessageSquare" size={14} />
                          <span>Review</span>
                        </button>
                      )}
                    </>
                  )}
                  
                  {booking.status === 'upcoming' && (
                    <>
                      <button className="flex items-center space-x-2 px-4 py-2 border border-blue-200 text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors duration-200">
                        <Icon name="Edit" size={14} />
                        <span>Modify</span>
                      </button>
                      
                      <button className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-md text-sm font-medium hover:bg-red-50 transition-colors duration-200">
                        <Icon name="X" size={14} />
                        <span>Cancel</span>
                      </button>
                    </>
                  )}
                  
                  {booking.status === 'cancelled' && (
                    <button
                      onClick={() => handleRebook(booking)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
                    >
                      <Icon name="Plus" size={14} />
                      <span>Rebook</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {filteredBookings.length === 0 && (
        <div className="text-center py-12 bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <Icon name="Calendar" size={48} className="text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No sessions found</h3>
          <p className="text-slate-500">
            {selectedFilter === 'all' ? 'You haven\'t booked any sessions yet.' : `No ${selectedFilter} sessions found.`}
          </p>
          <button className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200">
            Book Your First Session
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;